import {
	NotificationCard,
	OneColumnStack,
	RHFSelect,
	RHFTextField,
	Section,
} from "@arp/react-ndbx";
import React, { useEffect, useState } from "react";

import { Box, Divider, Fade, Stack } from "@mui/material";
import Backdrop from "@mui/material/Backdrop";
import CircularProgress from "@mui/material/CircularProgress";

//** Config */
import { Application, URLS } from "../../../config/const/enums";
import { yourDetailsContactDisclaimers } from "../../../config/disclaimers/disclaimers.jsx";
import {
	insuranceType,
	states,
} from "../../../config/dropdownSelectValues/selectValues";
import { yourDetailsContactNotification } from "../../../config/notifications/pageNotifications";
import { yourDetailsContactFieldNames } from "../../../config/pageInfo/pageFieldName";
import { yourDetailsContactTooltips } from "../../../config/tooltips/pageTooltips";

//** MUI Components */
import { Typography } from "@mui/material";

//** App components */
import Page from "../../../components/Page";
import ButtonNavigationGroup from "../../../components/organisms/ButtonNavigationGroup.jsx";
import TextFieldNotification from "../../../components/organisms/TextFieldNotification.jsx";
import YourDetailsHeader from "../../../components/pageLevel/YourDetailsHeader.jsx";
import ContactInformationQuestions from "../../../components/pageLevel/contactInformation/ContactInformationQuestions.jsx";
import CustomerConsent from "../../../components/pageLevel/sharedComponents/CustomerConsent";

//** Context */
import { useOutletContext } from "react-router-dom";
import useAuth from "../../../hooks/useAuth.ts";
import usePreQualification from "../../../hooks/usePreQualification.ts";
import usePreQualificationPages from "../../../hooks/usePreQualificationPages.ts";
import useSharedData from "../../../hooks/useSharedData.ts";

//** Hooks */
import { useValidatePromoCode } from "../../../hooks//dataHooks/useValidatePromoCode.js";
import { trackifyPageChange } from "@allianz/tracking";
import { isPromoCodeValid } from "../../../utilities/utils.js";

// import { method } from "lodash";

export default function YourDetailsContact({ person = "personOne" }) {
	const {
		methods: [methods],
		showSecond: [showSecond],
		errors: [errors],
	} = useOutletContext();

	const { setValue, resetField } = { ...methods };

	//* Access context
	const { state } = usePreQualificationPages();
	const { currentPage } = { ...state };
	const { dataSet, handleInputChange } = usePreQualification();

	const { personOne, personTwo } = { ...dataSet };
	const { getOktaAccessToken } = useAuth();
	const { pdsUrl, retrieveQuoteJourney } = useSharedData();

	//?? Contact Number */
	//?? Show Important information notification */
	const [contactNotification, setContactNotification] = useState(false);

	const [insuranceTypeItems, setInsuranceTypeItems] = useState([]);
	const [insuranceTypeDefaultValues, setInsuranceTypeDefaultValues] = useState(
		dataSet[person].otherAllianzInsuranceList
	);
	const [notificationItems, setNotificationItems] = useState([]);
	const [stateItems, setStateItems] = useState([]);
	const [promoCode, setPromoCode] = useState(null);
	const [showPromoCodeError, setShowPromoCodeError] = useState(false);

	useEffect(() => {
		setInsuranceTypeItems(insuranceType);
		setInsuranceTypeDefaultValues(dataSet[person].otherAllianzInsuranceList);
		setStateItems(states);
		setNotificationItems(
			yourDetailsContactNotification({ pdsURL: pdsUrl }).notificationInfo
		);

		dataSet[person]?.contactNumber?.toString().length > 0
			? setContactNotification(true)
			: setContactNotification(false);
	}, []);

	const [oktaToken, setOktaToken] = useState(null);
	useEffect(() => {
		const loadOktaTokenForCC = async () => {
			try {
				if (!dataSet) return;
				if (dataSet?.application === Application.CONTACT_CENTER) {
					let oktaAccessToken = await getOktaAccessToken();

					const { accessToken } = { ...oktaAccessToken };

					setOktaToken(accessToken);
				}
			} catch (e) {
				console.error(e);
			}
		};
		loadOktaTokenForCC();
	}, []);

	const {
		data,
		refetch,
		isFetching,
		isLoading,
		error: promoError,
	} = useValidatePromoCode({
		promoCode: promoCode,
		oktaToken: oktaToken,
	});

	useEffect(() => {
		try {
			let validPromoCode = false;
			if (data != null && data?.status !== 200) {
				setShowPromoCodeError(true);
				validPromoCode = false;
			} else if (promoCode == null) {
				setShowPromoCodeError(false);
				validPromoCode = false;
			} else {
				setShowPromoCodeError(false);
				validPromoCode = true;
			}

			handleInputChange({ name: "promoCodeValid", value: validPromoCode });
		} catch (e) {
			console.error(e);
		}
	}, [data, promoCode]);

	const handlePromoOnBlur = (e) => {
		e.preventDefault();
		if (e?.target?.value == null || e?.target?.value === "") {
			setPromoCode(null);
			return;
		}

		// When we on blur the promo code
		// and there is a value to check, then ensure that the
		// promo code is valid.
		const { name, value } = e.target;
		const promoCodeValidFlag = isPromoCodeValid(errors);
		if (promoCodeValidFlag) {
			setPromoCode(value);
		} else {
			setPromoCode(null);
		}
	};

	const [open, setOpen] = useState(false);
	useEffect(() => {
		setOpen(isFetching);
	}, [isFetching]);

	const PromoCodeError = () => {
		return (
			<Stack>
				<Typography>
					The code you've entered is not active or has expired. Check the
					promotional code you've been given and re-enter. For assistance,
					contact us on {URLS.CONTACT_US_PHONE_NUMBER}.
				</Typography>
			</Stack>
		);
	};

	useEffect(() => {
		if (dataSet?.customerRetrieveQuoteLoaded === false || state == null) return;

		setValue(
			yourDetailsContactFieldNames(person).EMAIL_ADDRESS,
			dataSet?.[person]?.emailAddress,
			{ shouldDirty: true }
		);

		setValue(
			yourDetailsContactFieldNames(person).CONTACT_NUMBER,
			dataSet?.[person]?.contactNumber,
			{ shouldDirty: true }
		);

		setContactNotification(true);

		setValue(
			yourDetailsContactFieldNames(person).STATE,
			dataSet?.[person]?.state,
			{ shouldDirty: true }
		);

		setValue(
			yourDetailsContactFieldNames(person).PROMO_CODE,
			dataSet?.[person]?.promoCode,
			{ shouldDirty: true }
		);

		setValue(
			yourDetailsContactFieldNames(person).TOBACCO_QUESTION,
			dataSet[person]?.tobaccoQuestion
		);

		setValue(
			yourDetailsContactFieldNames(person).OTHER_ALLIANZ_INSURANCE_QUESTION,
			dataSet[person]?.otherAllianzInsuranceQuestion
		);
		setValue(
			yourDetailsContactFieldNames(person).OTHER_ALLIANZ_INSURANCE_LIST,
			dataSet[person]?.otherAllianzInsuranceList
		);

		setValue(
			yourDetailsContactFieldNames(person).COMMUNICATION_CONSENT,
			dataSet[person]?.callbackConsent
		);
	}, [dataSet?.customerRetrieveQuoteLoaded]);

	useEffect(() => {
		trackifyPageChange(currentPage?.identifier?.toLowerCase());
	}, []);

	//** Communication Consent */
	const isContactCenter = dataSet?.application === Application.CONTACT_CENTER;
	let shouldHideConsent =
		(retrieveQuoteJourney === true &&
			dataSet[person]?.callbackConsent == null) ||
		isContactCenter === true;
	const isProdReady = true; //? NB: Only set to true, when ready to go in to Production
	//** Communication Consent */

	//--------------------------------------------------------------------------
	return (
		<Page title={currentPage.title} identifier={currentPage.identifier}>
			<Backdrop
				sx={{ color: "#008ED6", zIndex: (theme) => theme.zIndex.drawer + 1 }}
				open={open}
				// onClick={handleClose}
			>
				<CircularProgress color="inherit" />
			</Backdrop>

			<YourDetailsHeader />
			{showSecond ? (
				<Section mt={8}>
					<Typography variant="h3-bold">
						Applicant 1: {personOne.firstName} {personOne.surname}
					</Typography>
				</Section>
			) : null}
			<OneColumnStack id="your-details-contact-question-layout-section">
				<Section md={12} mt={{ sm: 2, md: 4 }}>
					<RHFTextField
						className="subvariant-text-input"
						variant="standard"
						label="Email address"
						name={yourDetailsContactFieldNames(person).EMAIL_ADDRESS}
						defaultValue={dataSet[person].emailAddress}
						// type="email"
					/>
				</Section>
				<Section md={12} mt={{ sm: 2, md: 4 }}>
					<TextFieldNotification
						className="subvariant-text-input"
						variant="standard"
						label="Mobile number"
						name={yourDetailsContactFieldNames(person).CONTACT_NUMBER}
						defaultValue={dataSet[person].contactNumber}
						type="phone"
						showTooltip={true}
						tooltipText={yourDetailsContactTooltips.MOBILE_NUMBER_TOOLTIP}
						onFocus={() => setContactNotification(true)}
						showNotification={contactNotification}
						notificationCard={
							yourDetailsContactNotification({ pdsURL: null }).notificationInfo
								.CONTACT_NUMBER_INFORMATION_NOTIFICATION
						}
					/>
				</Section>
				<Section
					id="title-select-section"
					md={12}
					mt={3}
					align="left"
					sx={{ outline: "0px blue solid" }}
				>
					<RHFSelect
						name={yourDetailsContactFieldNames(person).STATE}
						label="State"
						variant="standard"
						defaultValue={dataSet[person]?.state}
						items={stateItems}
					/>
				</Section>
				<Section md={12} mt={{ sm: 2, md: 4 }}>
					<RHFTextField
						className="subvariant-text-input"
						variant="standard"
						label="Promotional code"
						name={yourDetailsContactFieldNames(person).PROMO_CODE}
						defaultValue={dataSet[person].promoCode}
						showTooltip={true}
						tooltipText={yourDetailsContactTooltips.PROMOTIONAL_CODE_TOOLTIP}
						onBlur={(e) => handlePromoOnBlur(e)}
					/>
					<Box
						sx={{
							mt: 1,
							ml: "0px !important",
							display: showPromoCodeError === true ? "block" : "none",
						}}
					>
						<Fade in={true} timeout={500}>
							<Box>
								<NotificationCard
									cardType="error"
									title={""}
									message={PromoCodeError()}
								/>
							</Box>
						</Fade>
					</Box>
				</Section>
			</OneColumnStack>
			{/* {shouldHideConsent === false && isProdReady === true ? (
				<OneColumnStack data-identifier={"customer-consent"}>
					<>
						<Section
							sm={12}
							mt={{ sm: 2, md: 4 }}
							data-identifier={"customer-consent"}
						>
							<CustomerConsent
								person={"personOne"}
								dataSet={dataSet}
								fieldName={
									yourDetailsContactFieldNames(person).COMMUNICATION_CONSENT
								}
							/>
						</Section>
						<Section
							xs={12}
							md={12}
							mt={{ xs: 2, md: 2 }}
							sx={{ lineHeight: 0, textAlign: "left" }}
						>
							<Typography variant="label-medium-regular">
								{
									yourDetailsContactDisclaimers
										.PRIVACY_NOTICE_CONTACT_DISCLAIMER.note
								}
							</Typography>
						</Section>
					</>
				</OneColumnStack>
			) : null} */}
			{shouldHideConsent === false && isProdReady === true ? (
				<OneColumnStack data-identifier={"customer-consent"}>
					<>
						<Section
							xs={12}
							md={12}
							mt={{ xs: 2, md: 2 }}
							sx={{ lineHeight: 1.3, textAlign: "left" }}
						>
							<Typography variant="label-medium-regular">
								{
									yourDetailsContactDisclaimers
										.PRIVACY_NOTICE_CONTACT_DISCLAIMER.pre_approved_consent_note
								}
							</Typography>
						</Section>
					</>
				</OneColumnStack>
			) : null}
			<ContactInformationQuestions
				person={"personOne"}
				dataSet={dataSet}
				insuranceTypeItems={insuranceTypeItems}
				notificationItems={notificationItems}
			/>
			<Section mt={8}>
				<Divider />
			</Section>
			{showSecond ? (
				<Box>
					<Section
						mt={8}
						sx={{
							backgroundColor: "#fafafa",
						}}
					>
						<Section mt={3}>
							<Typography variant="h3-bold">
								Applicant 2: {personTwo.firstName} {personTwo.surname}
							</Typography>
						</Section>
						<ContactInformationQuestions
							person={"personTwo"}
							dataSet={dataSet}
							insuranceTypeItems={insuranceTypeItems}
							notificationItems={notificationItems}
						/>
					</Section>
					<Section mt={8}>
						<Divider />
					</Section>
				</Box>
			) : null}
			<ButtonNavigationGroup />
		</Page>
	);
}
